import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Calendar from '../../components/common/Calendar';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

const GuestContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const AddSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const InfoBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.875rem;
  color: #856404;
  line-height: 1.6;
  margin-bottom: 1.25rem;
`;

const GuestHistorySection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const GuestCard = styled.div`
  padding: 1.5rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const GuestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const GuestName = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
`;

const GuestDate = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
`;

const GuestInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #495057;
`;

const InfoValue = styled.span`
  color: #6c757d;
`;

const CalendarSection = styled.div`
  margin-bottom: 1.25rem;
`;

const CalendarLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
`;

const CalendarWrapper = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 1rem;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const CalendarInfo = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  margin-top: 0.75rem;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

interface GuestRecord {
  id: number;
  guestName: string;
  guestPhone: string;
  gender: string;
  electionStatus: string;
  participateDate: string;
  applyDate: string;
}

const MemberGuestApply: React.FC = () => {
  const { user } = useAuth();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [formData, setFormData] = useState({
    guestName: '',
    guestPhone: '',
    birthDate: '',
    gender: '',
    electionStatus: '',
    participateDate: ''
  });

  const [schedules, setSchedules] = useState<Array<{ schedule_date: string }>>([]);
  const [myGuests, setMyGuests] = useState<GuestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // 운동일정 목록 조회
  const fetchSchedules = async () => {
    try {
      const response = await apiClient.get('/schedules');
      if (response.data.success) {
        setSchedules(response.data.data || []);
      }
    } catch (error) {
      console.error('운동일정 조회 실패:', error);
    }
  };

  // 내가 데려온 게스트 목록 조회
  const fetchMyGuests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/guests');
      if (response.data.success) {
        // 현재 로그인한 회원이 데려온 게스트만 필터링
        const myGuestList = (response.data.data || [])
          .filter((guest: any) => guest.invited_by_user_no === user?.user_no)
          .map((guest: any) => ({
            id: guest.guest_no,
            guestName: guest.name,
            guestPhone: guest.phone,
            gender: guest.gender,
            electionStatus: guest.election_status,
            participateDate: guest.selected_date,
            applyDate: guest.apply_date
          }));
        setMyGuests(myGuestList);
      }
    } catch (error) {
      console.error('게스트 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchMyGuests();
  }, [user]);

  const scheduledDates = schedules.map(s => s.schedule_date);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'guestPhone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    // 생년월일: 숫자만 입력, 6자리 제한
    if (field === 'birthDate') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  const handleDateClick = (dateStr: string) => {
    // 오늘 이전 날짜 체크
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateStr);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      alert('오늘보다 이전 날짜는 선택할 수 없습니다.');
      return;
    }
    
    // 운동 일정이 있는 날짜만 선택 가능
    if (scheduledDates.includes(dateStr)) {
      setFormData(prev => ({
        ...prev,
        participateDate: dateStr
      }));
    } else {
      alert('운동 일정이 있는 날짜만 선택할 수 있습니다.');
    }
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 일정 불러오기
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.guestName.trim()) {
      window.alert('게스트 이름을 입력해주세요.');
      return;
    }

    if (!formData.guestPhone.trim()) {
      window.alert('게스트 전화번호를 입력해주세요.');
      return;
    }

    if (!formData.birthDate.trim()) {
      window.alert('생년월일을 입력해주세요.');
      return;
    }

    if (formData.birthDate.length !== 6) {
      window.alert('생년월일은 6자리로 입력해주세요. (예: 231105)');
      return;
    }

    if (!formData.gender) {
      window.alert('성별을 선택해주세요.');
      return;
    }

    if (!formData.electionStatus) {
      window.alert('선출 여부를 선택해주세요.');
      return;
    }

    if (!formData.participateDate) {
      window.alert('참가 희망일을 선택해주세요.');
      return;
    }

    // 운동 일정이 있는 날짜인지 확인
    if (!scheduledDates.includes(formData.participateDate)) {
      window.alert('운동 일정이 있는 날짜만 선택할 수 있습니다.');
      return;
    }

    if (!user?.user_no) {
      window.alert('로그인 정보를 확인할 수 없습니다.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiClient.post('/guests', {
        name: formData.guestName,
        phone: formData.guestPhone,
        birth_date: formData.birthDate,
        gender: formData.gender,
        election_status: formData.electionStatus,
        selected_date: formData.participateDate,
        invited_by_user_no: user.user_no // 회원이 데려온 게스트
      });

      if (response.data.success) {
        const guestData = response.data.data;
        const applyCount = guestData.apply_count || 1;
        const feeRequired = guestData.fee_required || false;
        
        let alertMessage = '게스트 신청이 완료되었습니다!';
        if (feeRequired) {
          alertMessage += `\n\n이번 신청은 ${applyCount}회차로, 게스트 비용 8,000원이 필요합니다.`;
        } else {
          alertMessage += `\n\n이번 신청은 ${applyCount}회차로 무료입니다.`;
        }
        
        window.alert(alertMessage);
        
        // 폼 초기화
        setFormData({
          guestName: '',
          guestPhone: '',
          birthDate: '',
          gender: '',
          electionStatus: '',
          participateDate: ''
        });

        // 게스트 목록 새로고침
        fetchMyGuests();
      } else {
        window.alert('게스트 신청에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('게스트 신청 오류:', error);
      window.alert(error.response?.data?.detail || '게스트 신청 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="게스트 신청">
      <GuestContainer>
        <Title>게스트 신청</Title>
        
        <AddSection>
          <SectionTitle>게스트 데려오기</SectionTitle>
          
          <InfoBox>
            💡 {user?.name}님이 게스트를 데려오시는 경우 여기서 신청해주세요.
            <br />
            게스트 정보를 입력하시면 참가 이력으로 기록됩니다.
          </InfoBox>
          
          <Form onSubmit={handleSubmit}>
            <Input
              label="게스트 이름"
              type="text"
              value={formData.guestName}
              onChange={(e) => handleInputChange('guestName', e.target.value)}
              placeholder="게스트 이름을 입력하세요"
              required
            />
            
            <Input
              label="게스트 전화번호"
              type="tel"
              value={formData.guestPhone}
              onChange={(e) => handleInputChange('guestPhone', e.target.value)}
              placeholder="010-0000-0000"
              required
            />
            
            <Input
              label="생년월일"
              type="text"
              value={formData.birthDate}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              placeholder="6자리 (예: 231105)"
              required
            />
            
            <RadioGroup>
              <RadioLabel>성별 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="남자"
                    checked={formData.gender === '남자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  남자
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="여자"
                    checked={formData.gender === '여자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  여자
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
            
            <RadioGroup>
              <RadioLabel>선출 여부 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="선출"
                    checked={formData.electionStatus === '선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  선출
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="비선출"
                    checked={formData.electionStatus === '비선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  비선출
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
            
            <CalendarSection>
              <CalendarLabel>
                참가 희망일 <span style={{ color: '#dc3545' }}>*</span>
                {formData.participateDate && (
                  <span style={{ marginLeft: '0.5rem', color: '#0f5132', fontWeight: 700 }}>
                    : {formData.participateDate} 선택됨
                  </span>
                )}
              </CalendarLabel>
              <CalendarWrapper>
                <Calendar
                  year={currentYear}
                  month={currentMonth}
                  scheduledDates={scheduledDates}
                  selectedDate={formData.participateDate}
                  onDateClick={handleDateClick}
                  onMonthChange={handleMonthChange}
                />
                <CalendarInfo>
                  ✓ 검은색 테두리 날짜는 운동 일정이 있는 날입니다.
                  <br />
                  ✓ 운동 일정이 있는 날만 게스트 신청이 가능합니다.
                </CalendarInfo>
              </CalendarWrapper>
            </CalendarSection>
            
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              disabled={submitting}
            >
              {submitting ? '신청 중...' : '게스트 신청하기'}
            </Button>
          </Form>
        </AddSection>
        
        <GuestHistorySection>
          <SectionTitle>내가 데려온 게스트 ({myGuests.length}명)</SectionTitle>
          
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : myGuests.length > 0 ? (
            myGuests.map(guest => (
              <GuestCard key={guest.id}>
                <GuestHeader>
                  <GuestName>{guest.guestName}</GuestName>
                  <GuestDate>참가일: {guest.participateDate}</GuestDate>
                </GuestHeader>
                
                <GuestInfo>
                  <InfoItem>
                    <InfoLabel>📞 전화번호:</InfoLabel>
                    <InfoValue>{guest.guestPhone}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>👤 성별:</InfoLabel>
                    <InfoValue>{guest.gender}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>✅ 선출:</InfoLabel>
                    <InfoValue>{guest.electionStatus}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>📅 신청일:</InfoLabel>
                    <InfoValue>{guest.applyDate}</InfoValue>
                  </InfoItem>
                </GuestInfo>
              </GuestCard>
            ))
          ) : (
            <EmptyState>아직 데려온 게스트가 없습니다.</EmptyState>
          )}
        </GuestHistorySection>
      </GuestContainer>
    </DashboardLayout>
  );
};

export default MemberGuestApply;

